import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ChevronDown, ChevronUp, Search, Package } from 'lucide-react';
import { Elevation, ExteriorPalette } from '../../types';
import { useTheme } from '../styles/ThemeContext';
import { ExteriorPackageOption } from './ExteriorPackagePane';
const PKG_LETTERS = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j'];
interface ExteriorGalleryProps {
  elevations: Elevation[];
  selectedElevationId: string | null;
  onSelectElevation: (elevation: Elevation) => void;
  palettes?: ExteriorPalette[];
  selectedPaletteId?: string | null;
  onSelectPalette?: (palette: ExteriorPalette) => void;
  modelName?: string;
  // Packages for the currently-selected elevation (empty/undefined = no package row)
  packages?: ExteriorPackageOption[];
  selectedPackageId?: string | null;
  onSelectPackage?: (pkg: ExteriorPackageOption) => void;
}
type TabId = 'elevations' | 'palettes';
const getElevationPrice = (index: number): number => {
  if (index === 0) return 0;
  if (index <= 2) return 1500;
  if (index <= 4) return 3500;
  return 5500;
};
const getPalettePrice = (index: number): number => {
  if (index === 0) return 0;
  if (index <= 2) return 500;
  if (index <= 4) return 1200;
  return 2000;
};
const formatNumber = (n: number): string => n.toString().padStart(2, '0');
function MarqueeName({
  name,
  isDark
}: {
  name: string;
  isDark: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const [overflow, setOverflow] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  useEffect(() => {
    if (containerRef.current && textRef.current) {
      const diff = textRef.current.scrollWidth - containerRef.current.clientWidth;
      setOverflow(diff > 2 ? diff + 8 : 0);
    }
  }, [name]);
  return <div ref={containerRef} className="overflow-hidden px-1" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} data-id="element-694">
      <span ref={textRef} className={`block whitespace-nowrap text-[9px] font-bold text-center uppercase tracking-wider ${isDark ? 'text-white/90' : 'text-gray-800'}`} style={{
      transform: isHovered && overflow > 0 ? `translateX(-${overflow}px)` : 'translateX(0)',
      transition: isHovered && overflow > 0 ? `transform ${Math.max(1, overflow / 40)}s ease-in-out` : 'transform 0.3s ease-out'
    }} title={name} data-id="element-695">
        {name}
      </span>
    </div>;
}
export function ExteriorGallery({
  elevations,
  selectedElevationId,
  onSelectElevation,
  palettes = [],
  selectedPaletteId,
  onSelectPalette,
  modelName,
  packages = [],
  selectedPackageId,
  onSelectPackage
}: ExteriorGalleryProps) {
  const {
    isDark
  } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState<TabId>('elevations');
  const [search, setSearch] = useState('');
  // Collapse the package tray to a single summary row once a package is chosen
  const [packagesCollapsed, setPackagesCollapsed] = useState(false);
  // Auto-collapse the moment a package is selected; re-expand when elevation changes
  useEffect(() => {
    setPackagesCollapsed(!!selectedPackageId);
  }, [selectedPackageId]);
  useEffect(() => {
    setPackagesCollapsed(false);
  }, [selectedElevationId]);
  const selectedPackage = packages.find(p => p.id === selectedPackageId);
  const selectedPackageIndex = packages.findIndex(p => p.id === selectedPackageId);
  const filteredElevations = elevations.filter(e => e.name.toLowerCase().includes(search.toLowerCase()));
  const filteredPalettes = palettes.filter(p => p.name.toLowerCase().includes(search.toLowerCase()));
  const tabs: {
    id: TabId;
    label: string;
    count: number;
    show: boolean;
  }[] = [{
    id: 'elevations',
    label: 'Elevations',
    count: elevations.length,
    show: true
  }, {
    id: 'palettes',
    label: 'Palettes',
    count: palettes.length,
    show: palettes.length > 0
  }];
  return <motion.div initial={{
    y: 100,
    opacity: 0
  }} animate={{
    y: 0,
    opacity: 1
  }} exit={{
    y: 100,
    opacity: 0
  }} transition={{
    duration: 0.4,
    ease: [0.22, 1, 0.36, 1]
  }} className="absolute bottom-16 left-[260px] right-6 z-30 flex justify-center px-6" data-id="element-696">
      <div className={`w-full max-w-[1100px] rounded-2xl backdrop-blur-2xl border shadow-2xl overflow-hidden ${isDark ? 'bg-slate-900/80 border-white/10' : 'bg-white/85 border-white/40'}`} data-id="element-697">
        {/* Header */}
        <div className={`flex items-center justify-between gap-4 px-5 py-3 border-b ${isDark ? 'border-white/10' : 'border-gray-200/60'}`} data-id="element-698">
          {/* Tabs */}
          <div className="flex items-center gap-1" data-id="element-699">
            {tabs.filter(t => t.show).map(tab => {
            const isActive = activeTab === tab.id;
            return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`relative flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${isActive ? isDark ? 'bg-white/10 text-white' : 'bg-gray-900/5 text-gray-900' : isDark ? 'text-white/50 hover:text-white/80 hover:bg-white/5' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`} data-id="element-700">
                    <span className="uppercase tracking-wider" data-id="element-701">
                      {tab.label}
                    </span>
                    <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-mono ${isActive ? isDark ? 'bg-cyan-500/20 text-cyan-300' : 'bg-teal-500/15 text-teal-700' : isDark ? 'bg-white/5 text-white/40' : 'bg-gray-100 text-gray-400'}`} data-id="element-702">
                      {tab.count}
                    </span>
                  </button>;
          })}
          </div>

          {/* Title / Model */}
          <div className="flex-1 flex items-center justify-center min-w-0" data-id="element-703">
            <div className="text-center" data-id="element-704">
              <div className={`text-[10px] font-bold uppercase tracking-[0.18em] ${isDark ? 'text-white/40' : 'text-gray-400'}`} data-id="element-705">
                Customize Exterior
              </div>
              {modelName && <div className={`text-xs font-semibold truncate ${isDark ? 'text-white/80' : 'text-gray-700'}`} data-id="element-706">
                  {modelName}
                </div>}
            </div>
          </div>

          {/* Right controls */}
          <div className="flex items-center gap-2" data-id="element-707">
            <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'}`} data-id="element-708">
              <Search className={`w-3 h-3 ${isDark ? 'text-white/40' : 'text-gray-400'}`} data-id="element-709" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className={`bg-transparent outline-none text-xs w-24 ${isDark ? 'text-white placeholder:text-white/30' : 'text-gray-900 placeholder:text-gray-400'}`} data-id="element-710" />
            </div>
            <button onClick={() => setCollapsed(c => !c)} className={`p-1.5 rounded-lg transition-colors ${isDark ? 'text-white/50 hover:text-white hover:bg-white/10' : 'text-gray-400 hover:text-gray-900 hover:bg-gray-100'}`} title={collapsed ? 'Expand' : 'Collapse'} data-id="element-711">
              {collapsed ? <ChevronUp className="w-4 h-4" data-id="element-712" /> : <ChevronDown className="w-4 h-4" data-id="element-713" />}
            </button>
          </div>
        </div>

        {/* Body */}
        <AnimatePresence initial={false} data-id="element-714">
          {!collapsed && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.25,
          ease: [0.22, 1, 0.36, 1]
        }} className="overflow-hidden" data-id="element-715">
              {activeTab === 'elevations' && <div className="px-5 py-4" data-id="element-716">
                  <div className="flex gap-3 overflow-x-auto scrollbar-thin" data-id="element-717">
                    {filteredElevations.length === 0 ? <div className={`w-full text-center py-8 text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`} data-id="element-718">
                        No elevations match "{search}"
                      </div> : filteredElevations.map((elevation, index) => {
                const isSelected = selectedElevationId === elevation.id;
                const price = getElevationPrice(index);
                const isIncluded = price === 0;
                const formattedNum = formatNumber(index + 1);
                return <div key={elevation.id} className="group relative flex flex-col items-center gap-2 transition-all flex-shrink-0" data-id="element-719">
                            <button onClick={() => onSelectElevation(elevation)} className="relative flex flex-col items-center gap-1 transition-all text-left w-full" data-id="element-720">
                              <div className={`relative rounded-xl overflow-hidden border-2 transition-all duration-300 w-28 h-28 ${isSelected ? isDark ? 'border-cyan-500 shadow-[0_0_15px_rgba(6,182,212,0.4)]' : 'border-teal-500 shadow-[0_0_15px_rgba(20,184,166,0.4)]' : isDark ? 'border-white/10 group-hover:border-white/30' : 'border-gray-200 group-hover:border-gray-300'}`} data-id="element-721">
                                <img src={elevation.thumbnailUrl} alt={elevation.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" data-id="element-722" />

                                {/* Number badge top-left */}
                                <div className={`absolute top-1.5 left-1.5 z-10 min-w-[20px] h-5 flex items-center justify-center rounded-md px-1 backdrop-blur-md text-[10px] font-bold font-mono tabular-nums ${isDark ? 'bg-black/60 text-white/80 border border-white/20' : 'bg-white/80 text-gray-700 border border-gray-300/50'}`} data-id="element-723">
                                  {formattedNum}
                                </div>

                                {/* Name strip */}
                                <div className={`absolute bottom-0 inset-x-0 py-1 overflow-hidden backdrop-blur-md ${isDark ? 'bg-black/60' : 'bg-white/80'}`} data-id="element-724">
                                  <MarqueeName name={elevation.name} isDark={isDark} data-id="element-725" />
                                </div>

                                {/* Selected overlay */}
                                {isSelected && <div className={`absolute inset-0 flex items-center justify-center ${isDark ? 'bg-cyan-500/20' : 'bg-teal-500/20'}`} data-id="element-726">
                                    <div className={`rounded-full p-1 ${isDark ? 'bg-cyan-500' : 'bg-teal-500'}`} data-id="element-727">
                                      <Check className="w-3 h-3 text-white" data-id="element-728" />
                                    </div>
                                  </div>}
                              </div>

                              {/* Price pill */}
                              <div className="flex flex-col items-center gap-0.5 w-full px-1" data-id="element-729">
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isIncluded ? isDark ? 'text-white/30' : 'text-gray-400' : isDark ? 'bg-white/10 text-white/60' : 'bg-gray-100 text-gray-600'}`} data-id="element-730">
                                  {isIncluded ? 'Included' : `+${price.toLocaleString()}`}
                                </span>
                              </div>
                            </button>
                          </div>;
              })}
                  </div>

                  {/* Package reveal tray — shows when the selected elevation has packages */}
                  <AnimatePresence initial={false} data-id="element-731">
                    {selectedElevationId && packages.length > 0 && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: [0.22, 1, 0.36, 1]
              }} className="overflow-hidden" data-id="element-732">
                        <div className={`mt-4 pt-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200/60'}`} data-id="element-733">
                          {/* Sub-header */}
                          <div className="flex items-center gap-2 mb-3 px-0.5" data-id="element-734">
                            <Package className={`w-3.5 h-3.5 ${isDark ? 'text-cyan-400' : 'text-teal-600'}`} data-id="element-735" />
                            <span className={`text-[11px] font-bold uppercase tracking-wider ${isDark ? 'text-white/70' : 'text-gray-600'}`} data-id="element-736">
                              Packages
                            </span>
                            <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-mono ${isDark ? 'bg-white/5 text-white/40' : 'bg-gray-100 text-gray-400'}`} data-id="element-737">
                              {packages.length}
                            </span>
                            <div className="flex-1" data-id="element-738" />
                            {selectedPackage && <button onClick={() => setPackagesCollapsed(c => !c)} className={`flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md transition-colors ${isDark ? 'text-white/50 hover:text-white hover:bg-white/10' : 'text-gray-400 hover:text-gray-900 hover:bg-gray-100'}`} title={packagesCollapsed ? 'Show all packages' : 'Collapse to summary'} data-id="element-739">
                                {packagesCollapsed ? <>
                                    <ChevronDown className="w-3 h-3" data-id="element-740" />
                                    Show all
                                  </> : <>
                                    <ChevronUp className="w-3 h-3" data-id="element-741" />
                                    Collapse
                                  </>}
                              </button>}
                          </div>

                          {/* Collapsed summary row OR full package grid */}
                          <AnimatePresence initial={false} mode="wait" data-id="element-742">
                            {packagesCollapsed && selectedPackage ? <motion.div key="pkg-summary" initial={{
                      opacity: 0,
                      y: 4
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} exit={{
                      opacity: 0,
                      y: -4
                    }} transition={{
                      duration: 0.2
                    }} className={`flex items-center gap-3 rounded-xl border p-2 pr-3 ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'}`} data-id="element-743">
                                <div className={`relative w-12 h-12 rounded-lg overflow-hidden border-2 flex-shrink-0 ${isDark ? 'border-cyan-500' : 'border-teal-500'}`} data-id="element-744">
                                  <img src={selectedPackage.thumbnail} alt={selectedPackage.code} className="w-full h-full object-cover" data-id="element-745" />
                                  <div className={`absolute top-0.5 left-0.5 w-4 h-4 flex items-center justify-center rounded text-[9px] font-bold lowercase ${isDark ? 'bg-cyan-500 text-white' : 'bg-teal-600 text-white'}`} data-id="element-746">
                                    {PKG_LETTERS[selectedPackageIndex] ?? selectedPackageIndex + 1}
                                  </div>
                                </div>
                                <div className="min-w-0 flex-1" data-id="element-747">
                                  <div className={`text-[9px] font-bold uppercase tracking-wider ${isDark ? 'text-cyan-400' : 'text-teal-600'}`} data-id="element-748">
                                    Selected package
                                  </div>
                                  <div className={`text-sm font-bold truncate ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-749">
                                    {selectedPackage.code}
                                  </div>
                                </div>
                                <span className={`text-xs font-bold flex-shrink-0 ${isDark ? 'text-white/70' : 'text-gray-600'}`} data-id="element-750">
                                  {(selectedPackage.priceModifier ?? 0) === 0 ? 'Included' : `+$${selectedPackage.priceModifier!.toLocaleString()}`}
                                </span>
                                <button onClick={() => setPackagesCollapsed(false)} className={`flex-shrink-0 text-xs font-bold px-3 py-1.5 rounded-lg transition-colors ${isDark ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-gray-200 text-gray-900 hover:bg-gray-300'}`} data-id="element-751">
                                  Change
                                </button>
                              </motion.div> : <motion.div key="pkg-grid" initial={{
                      opacity: 0,
                      y: 4
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} exit={{
                      opacity: 0,
                      y: -4
                    }} transition={{
                      duration: 0.2
                    }} className="flex gap-3 overflow-x-auto scrollbar-thin" data-id="element-752">
                                {packages.map((pkg, i) => {
                        const isSelected = selectedPackageId === pkg.id;
                        const letter = PKG_LETTERS[i] ?? String(i + 1);
                        const pkgIncluded = (pkg.priceModifier ?? 0) === 0;
                        return <div key={pkg.id} className="group relative flex flex-col items-center gap-2 transition-all flex-shrink-0" data-id="element-753">
                                      <button onClick={() => onSelectPackage?.(pkg)} className="relative flex flex-col items-center gap-1 transition-all text-left w-full" data-id="element-754">
                                        <div className={`relative rounded-xl overflow-hidden border-2 transition-all duration-300 w-28 h-28 ${isSelected ? isDark ? 'border-cyan-500 shadow-[0_0_15px_rgba(6,182,212,0.4)]' : 'border-teal-500 shadow-[0_0_15px_rgba(20,184,166,0.4)]' : isDark ? 'border-white/10 group-hover:border-white/30' : 'border-gray-200 group-hover:border-gray-300'}`} data-id="element-755">
                                          <img src={pkg.thumbnail} alt={pkg.code} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" data-id="element-756" />

                                          {/* Letter badge top-left */}
                                          <div className={`absolute top-1.5 left-1.5 z-10 w-5 h-5 flex items-center justify-center rounded-md backdrop-blur-md text-[11px] font-bold lowercase ${isSelected ? isDark ? 'bg-cyan-500 text-white' : 'bg-teal-600 text-white' : isDark ? 'bg-black/60 text-white/80 border border-white/20' : 'bg-white/80 text-gray-700 border border-gray-300/50'}`} data-id="element-757">
                                            {letter}
                                          </div>

                                          {/* Name strip */}
                                          <div className={`absolute bottom-0 inset-x-0 py-1 overflow-hidden backdrop-blur-md ${isDark ? 'bg-black/60' : 'bg-white/80'}`} data-id="element-758">
                                            <MarqueeName name={pkg.code} isDark={isDark} data-id="element-759" />
                                          </div>

                                          {/* Selected overlay */}
                                          {isSelected && <div className={`absolute inset-0 flex items-center justify-center ${isDark ? 'bg-cyan-500/20' : 'bg-teal-500/20'}`} data-id="element-760">
                                              <div className={`rounded-full p-1 ${isDark ? 'bg-cyan-500' : 'bg-teal-500'}`} data-id="element-761">
                                                <Check className="w-3 h-3 text-white" data-id="element-762" />
                                              </div>
                                            </div>}
                                        </div>

                                        {/* Price pill */}
                                        <div className="flex flex-col items-center gap-0.5 w-full px-1" data-id="element-763">
                                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${pkgIncluded ? isDark ? 'text-white/30' : 'text-gray-400' : isDark ? 'bg-white/10 text-white/60' : 'bg-gray-100 text-gray-600'}`} data-id="element-764">
                                            {pkgIncluded ? 'Included' : `+${pkg.priceModifier!.toLocaleString()}`}
                                          </span>
                                        </div>
                                      </button>
                                    </div>;
                      })}
                              </motion.div>}
                          </AnimatePresence>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>}

              {activeTab === 'palettes' && palettes.length > 0 && <div className="px-5 py-4" data-id="element-765">
                  <div className="flex gap-3 overflow-x-auto scrollbar-thin" data-id="element-766">
                    {filteredPalettes.length === 0 ? <div className={`w-full text-center py-8 text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`} data-id="element-767">
                        No palettes match "{search}"
                      </div> : filteredPalettes.map((palette, index) => {
                const isSelected = selectedPaletteId === palette.id;
                const price = getPalettePrice(index);
                const isIncluded = price === 0;
                const formattedNum = formatNumber(index + 1);
                return <div key={palette.id} className="group relative flex flex-col items-center gap-2 transition-all flex-shrink-0" data-id="element-768">
                            <button onClick={() => onSelectPalette?.(palette)} className="relative flex flex-col items-center gap-1 transition-all text-left w-full" data-id="element-769">
                              <div className={`relative rounded-xl overflow-hidden border-2 transition-all duration-300 w-28 h-28 ${isSelected ? isDark ? 'border-cyan-500 shadow-[0_0_15px_rgba(6,182,212,0.4)]' : 'border-teal-500 shadow-[0_0_15px_rgba(20,184,166,0.4)]' : isDark ? 'border-white/10 group-hover:border-white/30' : 'border-gray-200 group-hover:border-gray-300'}`} data-id="element-770">
                                {/* Color stripes filling the thumbnail */}
                                <div className="absolute inset-0 flex" data-id="element-771">
                                  {palette.colors.map((color, idx) => <div key={idx} className="flex-1" style={{
                          backgroundColor: color
                        }} data-id="element-772" />)}
                                </div>

                                {/* Number badge */}
                                <div className={`absolute top-1.5 left-1.5 z-10 min-w-[20px] h-5 flex items-center justify-center rounded-md px-1 backdrop-blur-md text-[10px] font-bold font-mono tabular-nums ${isDark ? 'bg-black/60 text-white/80 border border-white/20' : 'bg-white/80 text-gray-700 border border-gray-300/50'}`} data-id="element-773">
                                  {formattedNum}
                                </div>

                                {/* Name strip */}
                                <div className={`absolute bottom-0 inset-x-0 py-1 overflow-hidden backdrop-blur-md ${isDark ? 'bg-black/60' : 'bg-white/80'}`} data-id="element-774">
                                  <MarqueeName name={palette.name} isDark={isDark} data-id="element-775" />
                                </div>

                                {/* Selected overlay */}
                                {isSelected && <div className={`absolute inset-0 flex items-center justify-center ${isDark ? 'bg-cyan-500/20' : 'bg-teal-500/20'}`} data-id="element-776">
                                    <div className={`rounded-full p-1 ${isDark ? 'bg-cyan-500' : 'bg-teal-500'}`} data-id="element-777">
                                      <Check className="w-3 h-3 text-white" data-id="element-778" />
                                    </div>
                                  </div>}
                              </div>

                              {/* Price pill */}
                              <div className="flex flex-col items-center gap-0.5 w-full px-1" data-id="element-779">
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isIncluded ? isDark ? 'text-white/30' : 'text-gray-400' : isDark ? 'bg-white/10 text-white/60' : 'bg-gray-100 text-gray-600'}`} data-id="element-780">
                                  {isIncluded ? 'Included' : `+$${price.toLocaleString()}`}
                                </span>
                              </div>
                            </button>
                          </div>;
              })}
                  </div>
                </div>}
            </motion.div>}
        </AnimatePresence>
      </div>
    </motion.div>;
}